import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Package, Plus, Trash2, Loader2, Rocket, Save, PenLine,
  ShoppingBag, Briefcase, Users, Building2, GraduationCap, X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Product {
  id: string;
  name: string;
  category: string;
  description: string;
  price: number;
  features: string;
  enabled: boolean;
}

const CATEGORIES = [
  { value: "produit", label: "Produit", icon: ShoppingBag },
  { value: "prestation", label: "Prestation", icon: Briefcase },
  { value: "atelier", label: "Atelier", icon: GraduationCap },
  { value: "seminaire", label: "Séminaire", icon: Users },
  { value: "immobilier", label: "Immobilier", icon: Building2 },
];

const categoryMeta: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  produit: { label: "Produit", icon: ShoppingBag, color: "hsl(var(--chart-1))" },
  prestation: { label: "Prestation", icon: Briefcase, color: "hsl(var(--chart-2))" },
  atelier: { label: "Atelier", icon: GraduationCap, color: "hsl(var(--chart-3))" },
  seminaire: { label: "Séminaire", icon: Users, color: "hsl(var(--chart-4))" },
  immobilier: { label: "Immobilier", icon: Building2, color: "hsl(var(--chart-5))" },
};

export function AgentProducts() {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState({ name: "", category: "produit", description: "", price: 0, features: "" });
  const [newProduct, setNewProduct] = useState({ name: "", category: "produit", description: "", price: 0, features: "" });

  useEffect(() => { if (user) fetchProducts(); }, [user]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/nanocorp/products`, { credentials: "include" as RequestCredentials });
      if (res.ok) { const data = await res.json(); setProducts((data.products || []) as Product[]); }
    } catch {}
    setLoading(false);
  };

  const toggleProduct = async (id: string, enabled: boolean) => {
    await fetch(`${API_URL}/api/nanocorp/products/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      credentials: "include" as RequestCredentials, body: JSON.stringify({ enabled }),
    });
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, enabled } : p)));
    toast.success(enabled ? "Produit activé" : "Produit désactivé");
  };

  const addProduct = async () => {
    if (!user || !newProduct.name.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/nanocorp/products`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include" as RequestCredentials, body: JSON.stringify(newProduct),
      });
      if (res.ok) {
        const data = await res.json();
        setProducts((prev) => [...prev, data as Product]);
        setNewProduct({ name: "", category: "produit", description: "", price: 0, features: "" });
        setShowForm(false);
        toast.success("Produit ajouté !");
      }
    } catch { toast.error("Erreur lors de l'ajout"); }
    setSaving(false);
  };

  const startEdit = (product: Product) => {
    setEditingId(product.id);
    setEditData({ name: product.name, category: product.category, description: product.description, price: product.price, features: product.features });
  };

  const saveEdit = async () => {
    if (!editingId || !editData.name.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/nanocorp/products/${editingId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include" as RequestCredentials, body: JSON.stringify(editData),
      });
      if (res.ok) {
        setProducts((prev) => prev.map((p) => p.id === editingId ? { ...p, ...editData } : p));
        setEditingId(null);
        toast.success("Produit modifié !");
      }
    } catch { toast.error("Erreur de modification"); }
    setSaving(false);
  };

  const deleteProduct = async (id: string) => {
    await fetch(`${API_URL}/api/nanocorp/products/${id}`, { method: "DELETE", credentials: "include" as RequestCredentials });
    setProducts((prev) => prev.filter((p) => p.id !== id));
    toast.success("Produit supprimé");
  };

  const enabledCount = products.filter((p) => p.enabled).length;

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{enabledCount}</span> produit{enabledCount > 1 ? "s" : ""} actif{enabledCount > 1 ? "s" : ""} sur {products.length}
        </p>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="gap-1.5" data-testid="add-product-btn">
          <Plus className="h-3.5 w-3.5" /> Ajouter
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <ProductForm
          data={newProduct}
          onChange={setNewProduct}
          onSave={addProduct}
          onCancel={() => setShowForm(false)}
          saving={saving}
          title="Nouveau produit / prestation"
        />
      )}

      {/* Product list */}
      {products.length === 0 ? (
        <div className="text-center py-12">
          <Package className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground">Aucun produit ou prestation configuré.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {products.map((product) => {
            const meta = categoryMeta[product.category] || categoryMeta.produit;
            const CatIcon = meta.icon;
            const isEditing = editingId === product.id;

            if (isEditing) {
              return (
                <ProductForm
                  key={product.id}
                  data={editData}
                  onChange={setEditData}
                  onSave={saveEdit}
                  onCancel={() => setEditingId(null)}
                  saving={saving}
                  title={`Modifier : ${product.name}`}
                />
              );
            }

            return (
              <Card key={product.id} className={`border-border/50 transition-all ${!product.enabled ? "opacity-50" : ""}`} data-testid={`product-${product.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox checked={product.enabled} onCheckedChange={(checked) => toggleProduct(product.id, !!checked)} className="mt-1" />
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}20` }}>
                      <CatIcon className="h-4 w-4" style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-foreground">{product.name}</h3>
                        <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
                        {product.price > 0 && <Badge variant="secondary" className="text-[10px]">{product.price.toLocaleString("fr-FR")} €</Badge>}
                      </div>
                      {product.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{product.description}</p>}
                      {product.features && <p className="text-[10px] text-muted-foreground/70 mt-0.5 line-clamp-1">{product.features}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => startEdit(product)} data-testid={`edit-product-${product.id}`}>
                        <PenLine className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => deleteProduct(product.id)} data-testid={`delete-product-${product.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {enabledCount > 0 && (
        <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
          <CardContent className="p-4 flex items-center gap-3">
            <Rocket className="h-5 w-5 text-primary shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{enabledCount} offre{enabledCount > 1 ? "s" : ""} activée{enabledCount > 1 ? "s" : ""}</p>
              <p className="text-xs text-muted-foreground">Les agents utiliseront ces informations pour argumenter et vendre.</p>
            </div>
            <Button size="sm" className="gap-1.5" onClick={() => toast.success("Configuration des agents mise à jour")}><Rocket className="h-3.5 w-3.5" /> Lancer</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Reusable form for Add/Edit ──
function ProductForm({
  data, onChange, onSave, onCancel, saving, title,
}: {
  data: { name: string; category: string; description: string; price: number; features: string };
  onChange: (d: any) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  title: string;
}) {
  return (
    <Card className="border-primary/30 bg-primary/5" data-testid="product-form">
      <CardContent className="p-4 space-y-3">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input placeholder="Nom du produit / prestation" value={data.name} onChange={(e) => onChange({ ...data, name: e.target.value })} className="text-sm" data-testid="product-name-input" />
          <Select value={data.category} onValueChange={(v) => onChange({ ...data, category: v })}>
            <SelectTrigger className="text-sm" data-testid="product-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" placeholder="Prix (€)" value={data.price || ""} onChange={(e) => onChange({ ...data, price: parseFloat(e.target.value) || 0 })} className="text-sm" data-testid="product-price-input" />
        </div>
        <Textarea placeholder="Description commerciale courte..." value={data.description} onChange={(e) => onChange({ ...data, description: e.target.value })} className="text-sm min-h-[60px]" rows={2} data-testid="product-description-input" />
        <Textarea placeholder="Arguments de vente, caractéristiques clés..." value={data.features} onChange={(e) => onChange({ ...data, features: e.target.value })} className="text-sm min-h-[60px]" rows={2} data-testid="product-features-input" />
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={onCancel} data-testid="product-cancel-btn"><X className="h-3.5 w-3.5 mr-1" /> Annuler</Button>
          <Button size="sm" onClick={onSave} disabled={!data.name.trim() || saving} className="gap-1.5" data-testid="product-save-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
